import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '@/App';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AuthCallback() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    processCallback();
  }, []);

  const processCallback = async () => {
    // Get session_id from URL hash (Emergent Google Auth)
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    // Get code from URL params (Microsoft OAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const errorParam = urlParams.get('error');
    const state = urlParams.get('state') || '';
    
    // Check if this is an account connection flow (not login)
    const isAccountConnect = state.startsWith('connect_account_');
    const provider = state.startsWith('microsoft') || isAccountConnect ? 'microsoft' : 'google';

    // Handle errors
    if (errorParam) {
      const errorMsg = urlParams.get('error_description') || errorParam;
      setStatus('error');
      setError(errorMsg);
      
      // If this is a popup for account connection, notify parent
      if (isAccountConnect && window.opener) {
        window.opener.postMessage({ type: 'MICROSOFT_AUTH_ERROR', message: errorMsg }, '*');
        setTimeout(() => window.close(), 2000);
      }
      return;
    }

    try {
      let response;

      if (sessionIdMatch) {
        // Emergent Google Auth
        const sessionId = sessionIdMatch[1];
        response = await axios.post(`${API}/auth/google/callback`, {
          session_id: sessionId
        }, { withCredentials: true });
      } else if (code && isAccountConnect) {
        // Microsoft Account Connection (for existing logged-in users)
        // Need to use the existing token from localStorage
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found. Please login first.');
        }
        
        response = await axios.post(`${API}/auth/microsoft/connect-callback`, {
          code,
          redirect_uri: window.location.origin + '/auth/microsoft/callback'
        }, { 
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true 
        });
        
        // This is account connection, not login - notify parent window and close popup
        setStatus('success');
        if (window.opener) {
          window.opener.postMessage({ 
            type: 'MICROSOFT_AUTH_SUCCESS', 
            microsoft_email: response.data.microsoft_email 
          }, '*');
          setTimeout(() => window.close(), 1500);
        } else {
          // Not in a popup, redirect to email page
          setTimeout(() => {
            navigate('/email', { replace: true });
          }, 1500);
        }
        return;
        
      } else if (code && provider === 'microsoft') {
        // Microsoft OAuth Login (new user or existing user SSO login)
        response = await axios.post(`${API}/auth/microsoft/callback`, {
          code,
          redirect_uri: window.location.origin + '/auth/microsoft/callback'
        }, { withCredentials: true });
      } else {
        throw new Error('No authorization code or session received');
      }

      if (response.data?.requires_otp) {
        // First-login OTP gate — back to /login to enter the code.
        setStatus('success');
        setTimeout(() => {
          navigate('/login', {
            replace: true,
            state: {
              otpStep: {
                user_id: response.data.otp_user_id,
                email: response.data.otp_email,
                delivery_failed: response.data.otp_meta?.delivery_failed === true,
                source: 'microsoft',
              },
            },
          });
        }, 600);
        return;
      }

      if (response.data.access_token) {
        loginWithToken(response.data.access_token, response.data.user);
        setStatus('success');
        
        // Clear the URL and redirect
        setTimeout(() => {
          navigate('/', { replace: true, state: { user: response.data.user } });
        }, 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Authentication failed';
      setStatus('error');
      setError(errorMsg);
      
      // If this is a popup for account connection, notify parent
      if (isAccountConnect && window.opener) {
        window.opener.postMessage({ type: 'MICROSOFT_AUTH_ERROR', message: errorMsg }, '*');
        setTimeout(() => window.close(), 2000);
      }
    }
  };

  // Check if this is an account connection flow for UI messaging
  const urlParams = new URLSearchParams(window.location.search);
  const state = urlParams.get('state') || '';
  const isConnectFlow = state.startsWith('connect_account_');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800 border border-slate-700 p-10 rounded-xl shadow-xl text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-6"></div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {isConnectFlow ? 'Connecting Microsoft 365...' : 'Signing you in...'}
            </h2>
            <p className="text-slate-400">
              {isConnectFlow 
                ? 'Please wait while we link your Microsoft account.' 
                : 'Please wait while we complete your login.'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="h-16 w-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {isConnectFlow ? 'Microsoft 365 Connected!' : 'Login Successful!'}
            </h2>
            <p className="text-slate-400">
              {isConnectFlow 
                ? 'Closing this window...' 
                : 'Redirecting to dashboard...'}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-16 w-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {isConnectFlow ? 'Connection Failed' : 'Login Failed'}
            </h2>
            <p className="text-red-400 mb-4">{error}</p>
            {!isConnectFlow && (
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-white transition"
              >
                Back to Login
              </button>
            )}
            {isConnectFlow && (
              <p className="text-slate-400 text-sm">This window will close automatically...</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
